import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  OutlinedInput,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
} from "@mui/material";
import React, { FormEvent, useCallback, useEffect, useState } from "react";
import { IAdminLoginRequestBody } from "../../models/admin/adminAuth/adminAuth";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { adminForgotPassword, adminLogin } from "./adminAuthActions";
import { useNavigate } from "react-router-dom";
import { resetCommonSlice } from "../../store/common/CommonSlice";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LoginStyles from "./LoginStyles";
import { RootState } from "../../store/store";
import RenderVideoBanner from "../../common/RenderVideoBanner/RenderVideoBanner";
import { useTranslation } from "react-i18next";

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { uiSettings } = useAppSelector((state: RootState) => state.uiSettings);

  const { isLoading, isSuccess, calledReducerType, isError, message } =
    useAppSelector((state) => state.common);

  const [adminLoginRequestBody, setAdminLoginRequestBody] =
    useState<IAdminLoginRequestBody>({
      email: "",
      password: "",
      loginTime: "",
    });
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const [openForgotDialog, setOpenForgotDialog] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotEmailError, setForgotEmailError] = useState<string | null>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(resetCommonSlice());
      const { name, value } = event.target;
      setAdminLoginRequestBody((prevState) => ({
        ...prevState,
        [name]: value,
      }));
      setErrors((prevState) => ({ ...prevState, [name]: "" }));
    },
    [dispatch]
  );

  const handleClickShowPassword = () =>
    setShowPassword((prevState) => !prevState);

  const adminLoginValidator = useCallback((): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // const passwordRegex =
    //   /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    const errors: { email?: string; password?: string } = {};
    if (
      !adminLoginRequestBody.email ||
      adminLoginRequestBody.email.trim().length === 0
    ) {
      errors.email = "Email address cannot be blank";
    } else if (!emailRegex.test(adminLoginRequestBody.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (
      !adminLoginRequestBody.password ||
      adminLoginRequestBody.password.trim().length === 0
    ) {
      errors.password = "Password cannot be blank";
    }
    // else if (!passwordRegex.test(adminLoginRequestBody.password)) {
    //   errors.password = "Please enter a valid Password";
    // }
    setErrors(errors);
    return Object.keys(errors).length === 0;
  }, [adminLoginRequestBody]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (adminLoginValidator()) {
        dispatch(
          adminLogin({
            ...adminLoginRequestBody,
            loginTime: new Date().toString(),
          })
        );
      }
    },
    [adminLoginRequestBody, adminLoginValidator, dispatch]
  );

  const handleForgotPasswordClick = () => {
    setOpenForgotDialog(true);
  };

  const handleCloseForgotDialog = () => {
    setOpenForgotDialog(false);
    setForgotEmail("");
    setForgotEmailError("");
  };

  const handleSendResetLink = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!forgotEmail || !emailRegex.test(forgotEmail)) {
      setForgotEmailError("Please enter a valid email address");
      return;
    }

    dispatch(adminForgotPassword(forgotEmail));
  };

  useEffect(() => {
    if (isSuccess && calledReducerType === "adminAuth/adminForgotPassword") {
      setSnackbarOpen(true);
      setOpenForgotDialog(false);
    }
  }, [isSuccess, calledReducerType, navigate]);

  useEffect(() => {
    if (isSuccess && calledReducerType === "adminAuth/adminLogin") {
      navigate("/admin/dashboard");
    }
  }, [isSuccess, calledReducerType, navigate]);

  useEffect(() => {
    if (isError && calledReducerType === "adminAuth/adminForgotPassword") {
      setForgotEmailError(message);
    }
  }, [isError, calledReducerType, navigate]);

  return (
    <>
      <Box sx={LoginStyles.login}>
        <Box sx={LoginStyles.loginImage}>
          {uiSettings && uiSettings?.loginBannerImage && (
            <img
              src={`data:image/png;base64,${uiSettings.loginBannerImage}`}
              alt="Logo Preview"
            />
          )}

          {uiSettings && uiSettings?.loginBannerType === "video" && (
            <RenderVideoBanner
              videoUrl={uiSettings?.loginBannerVideoUrl as string}
            />
          )}
        </Box>
        <Box style={LoginStyles.loginCardContainer}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              width: "80%",
              paddingY: 2,
            }}
          >
            <Box sx={LoginStyles.logo}>
              {uiSettings && uiSettings?.logo && (
                <img
                  src={`data:image/png;base64,${uiSettings.logo}`}
                  alt="Logo Preview"
                />
              )}
            </Box>
            <Typography sx={LoginStyles.loginMessage}>
              Welcome, please login to continue
            </Typography>
            <form onSubmit={handleSubmit} style={LoginStyles.loginForm}>
              {isError && message ? (
                <Alert severity="error">{message}</Alert>
              ) : (
                <></>
              )}
              <FormControl>
                <InputLabel>Email</InputLabel>
                <OutlinedInput
                  type="text"
                  fullWidth
                  label="email"
                  name="email"
                  placeholder="Please enter your email address"
                  value={adminLoginRequestBody.email}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                />
                {errors.email ? (
                  <Typography sx={{ color: "red", marginTop: "8px" }}>
                    {errors.email}
                  </Typography>
                ) : (
                  <></>
                )}
              </FormControl>
              <FormControl>
                <InputLabel>Password</InputLabel>
                <OutlinedInput
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  name="password"
                  label="password"
                  placeholder="Please enter your password"
                  value={adminLoginRequestBody.password}
                  onChange={handleInputChange}
                  autoComplete="new-password"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                {errors.password ? (
                  <Typography sx={{ color: "red", marginTop: "8px" }}>
                    {errors.password}
                  </Typography>
                ) : (
                  <></>
                )}
              </FormControl>
              <Button
                variant="contained"
                type="submit"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {isLoading && calledReducerType === "adminAuth/adminLogin" && (
                  <CircularProgress color="info" size="20px" />
                )}
                {"Login"}
              </Button>
            </form>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginY: 1,
              }}
            >
              <Link
                component="button"
                onClick={handleForgotPasswordClick}
                sx={{
                  textDecoration: "none",
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {"Forgot password?"}
              </Link>
              <Link
                href="/"
                sx={{
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                {"Back to home"}
              </Link>
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={openForgotDialog}
        onClose={handleCloseForgotDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            width: "400px",
            maxWidth: "100%",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: 3,
            pb: 2,
          }}
        >
          {uiSettings && uiSettings?.logo && (
            <img
              src={`data:image/png;base64,${uiSettings.logo}`}
              alt="Logo"
              style={{
                width: "auto",
                height: "60px",
                marginBottom: "10px",
              }}
            />
          )}
        </Box>
        <DialogTitle
          sx={{
            textAlign: "start",
            color: "#0a0887",
            fontSize: "24px",
            fontWeight: 500,
            pt: 0,
          }}
        >
          Forgot Password
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 3, textAlign: "start", color: "#666" }}>
            Enter your email address and we'll send you a link to reset your
            password.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Email</InputLabel>
            <OutlinedInput
              type="email"
              fullWidth
              label="Email"
              value={forgotEmail}
              onChange={(e) => {
                setForgotEmail(e.target.value);
                setForgotEmailError("");
              }}
              placeholder="Please enter your email address"
            />
            {forgotEmailError && (
              <Typography sx={{ color: "red", mt: 1, fontSize: "14px" }}>
                {forgotEmailError}
              </Typography>
            )}
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", p: 3, gap: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSendResetLink}
            sx={{
              backgroundColor: "#00BCD4",
              "&:hover": {
                backgroundColor: "#00ACC1",
              },
            }}
          >
            Send Reset Link
          </Button>
          <Link
            component="button"
            onClick={handleCloseForgotDialog}
            sx={{
              color: "#00BCD4",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Back to Login
          </Link>
        </DialogActions>
      </Dialog>
      <Snackbar
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          variant="filled"
        >
          Link sent to mail
        </Alert>
      </Snackbar>
    </>
  );
};

export default Login;
